if (!customElements.get('lp-review-carousel')) {
  customElements.define('lp-review-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.viewport = this.querySelector('[data-review-viewport]');
      this.track = this.querySelector('[data-review-track]');
      this.cards = Array.from(this.track?.children || []);
      if (!this.viewport || !this.cards.length) return;
      this.controller = new AbortController();
      const events = { signal: this.controller.signal };
      this.previous = this.querySelector('[data-review-prev]');
      this.next = this.querySelector('[data-review-next]');
      this.dots = this.querySelector('[data-review-dots]');
      this.pause = this.querySelector('[data-review-pause]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = false;
      this.visible = false;
      this.positions = [];
      this.previous?.addEventListener('click', () => this.go(this.index - 1), events);
      this.next?.addEventListener('click', () => this.go(this.index + 1), events);
      this.pause?.addEventListener('click', () => {
        this.paused = !this.paused;
        this.pause.setAttribute('aria-pressed', String(this.paused));
        this.autoplay();
      }, events);
      this.viewport.addEventListener('keydown', (event) => {
        if (event.target !== this.viewport) return;
        const index = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.positions.length - 1 }[event.key];
        if (index === undefined) return;
        event.preventDefault();
        this.go(index);
      }, events);
      this.viewport.addEventListener('scroll', () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
      }, { ...events, passive: true });
      this.addEventListener('pointerdown', () => this.stopForInteraction(), events);
      this.addEventListener('focusin', () => this.stopForInteraction(), events);
      document.addEventListener('visibilitychange', () => this.autoplay(), events);
      this.motion.addEventListener('change', () => this.autoplay(), events);
      document.addEventListener('shopify:block:select', (event) => {
        const card = event.target.closest('.review-carousel-glass__card');
        if (!this.cards.includes(card)) return;
        this.stopForInteraction();
        this.viewport.scrollTo({ left: card.offsetLeft - this.cards[0].offsetLeft, behavior: 'instant' });
      }, events);
      this.resize = new ResizeObserver(() => this.layout());
      this.resize.observe(this.viewport);
      this.intersection = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.autoplay();
      }, { threshold: 0.2 });
      this.intersection.observe(this);
      this.layout();
    }

    layout() {
      const max = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const start = this.cards[0].offsetLeft;
      this.positions = [...new Set(this.cards.map(card => Math.round(Math.min(max, card.offsetLeft - start))))];
      if (this.dots) {
        const buttons = this.positions.map((position, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'review-carousel-glass__dot';
          button.setAttribute('aria-label', `${this.dataset.positionLabel} ${index + 1}`);
          button.setAttribute('aria-controls', this.track.id);
          button.addEventListener('click', () => this.go(index));
          return button;
        });
        this.dots.replaceChildren(...buttons);
        this.dots.hidden = max < 2;
      }
      this.update();
      this.autoplay();
    }

    update() {
      this.index = this.positions.reduce((best, position, index) =>
        Math.abs(position - this.viewport.scrollLeft) < Math.abs(this.positions[best] - this.viewport.scrollLeft) ? index : best, 0);
      if (this.previous) this.previous.disabled = this.viewport.scrollLeft <= 1;
      if (this.next) this.next.disabled = this.viewport.scrollLeft >= this.viewport.scrollWidth - this.viewport.clientWidth - 1;
      Array.from(this.dots?.children || []).forEach((button, index) => {
        button.classList.toggle('is-active', index === this.index);
        button.setAttribute('aria-current', String(index === this.index));
      });
    }

    go(index) {
      const position = this.positions[Math.max(0, Math.min(index, this.positions.length - 1))];
      this.viewport.scrollTo({ left: position, behavior: this.motion.matches ? 'instant' : 'smooth' });
    }

    stopForInteraction() {
      this.paused = true;
      this.pause?.setAttribute('aria-pressed', 'true');
      this.autoplay();
    }

    autoplay() {
      clearInterval(this.timer);
      if (this.dataset.autoplay !== 'true' || this.paused || !this.visible || document.hidden || this.motion.matches || this.positions.length < 2) return;
      this.timer = setInterval(() => this.go((this.index + 1) % this.positions.length), Number(this.dataset.interval));
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resize?.disconnect();
      this.intersection?.disconnect();
      clearInterval(this.timer);
      cancelAnimationFrame(this.frame);
      this.controller = null;
    }
  });
}
